import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  message: string;
  title?: string;
  type: ToastType;
}

interface ToastContextType {
  triggerToast: (message: string, title?: string, type?: ToastType) => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const triggerToast = useCallback(
    (message: string, title?: string, type: ToastType = 'info') => {
      const id = Date.now();

      setToasts((prev) => [...prev, { id, message, title, type }]);

      // Auto-dismiss after 3 seconds
      setTimeout(() => {
        removeToast(id);
      }, 3000);
    },
    [removeToast]
  );

  const showSuccess = useCallback(
    (message: string, title?: string) => {
      triggerToast(message, title, 'success');
    },
    [triggerToast]
  );

  const showError = useCallback(
    (message: string, title?: string) => {
      triggerToast(message, title, 'error');
    },
    [triggerToast]
  );

  const showWarning = useCallback(
    (message: string, title?: string) => {
      triggerToast(message, title, 'warning');
    },
    [triggerToast]
  );

  const showInfo = useCallback(
    (message: string, title?: string) => {
      triggerToast(message, title, 'info');
    },
    [triggerToast]
  );

  const getToastStyles = (type: ToastType): string => {
    const baseStyles =
      'fixed z-50 max-w-sm w-full p-4 rounded-xl shadow-lg animate-slide-down';

    switch (type) {
      case 'success':
        return `${baseStyles} bg-pagali-success text-white`;
      case 'error':
        return `${baseStyles} bg-pagali-error text-white`;
      case 'warning':
        return `${baseStyles} bg-pagali-warning text-white`;
      case 'info':
      default:
        return `${baseStyles} bg-pagali-info text-white`;
    }
  };

  const getIcon = (type: ToastType): string => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  const value: ToastContextType = {
    triggerToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={getToastStyles(toast.type)}
            onClick={() => removeToast(toast.id)}
            role="alert"
          >
            <div className="flex items-start gap-3">
              <span className="text-lg font-bold">{getIcon(toast.type)}</span>
              <div className="flex-1">
                {toast.title && (
                  <h4 className="font-semibold mb-1">{toast.title}</h4>
                )}
                <p className="text-sm opacity-95">{toast.message}</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeToast(toast.id);
                }}
                className="text-white/80 hover:text-white transition-colors"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastContext;
